<template>
  <div class="content">
    <div class="category-detail-shell">
      <card v-if="!editable" class="readonly-banner">
        <i class="tim-icons icon-alert-circle-exc" /> 이 카테고리는 수정할 수 없습니다. 현재 "인기 아티스트" 카테고리만 아티스트 추가/정렬/제거가 가능합니다.
      </card>
      <card class="toolbar-card">
        <div class="toolbar">
          <div class="toolbar-actions">
            <base-button @click="goList" type="default" size="sm">
              <span class="btn-content"><i class="tim-icons icon-minimal-left" /> 목록</span>
            </base-button>
            <base-button @click="artistAddVisible = true" type="default" :disabled="!editable">
              <span class="btn-content"><i class="tim-icons icon-simple-add" /> 아티스트 추가</span>
            </base-button>
            <base-button @click="save" type="success" :disabled="!editable">
              <span class="btn-content"> 저장</span>
            </base-button>
          </div>
          <div v-if="editable" class="toolbar-hint">
            <span class="hint-drag-icon"><i class="tim-icons icon-minimal-up" /><i class="tim-icons icon-minimal-down" /></span>
            행을 드래그해서 순서를 바꿀 수 있습니다 · 추가/정렬/제거 후 반드시 저장을 눌러야 반영됩니다
          </div>
        </div>
      </card>
      <card class="artist-list-card">
        <draggable
          v-model="units"
          handle=".artist-row-handle"
          group="people"
          ghost-class="artist-row-ghost"
          drag-class="artist-row-dragging"
          :disabled="!editable"
        >
          <div class="artist-row" v-for="(item, index) in units" :key="item.unitId">
            <div class="artist-row-handle" :class="{ 'artist-row-handle-disabled': !editable }">
              <i class="tim-icons icon-minimal-up" />
              <i class="tim-icons icon-minimal-down" />
            </div>
            <div class="artist-row-order">{{ index + 1 }}</div>
            <img v-if="item.imageFileUrl" class="artist-row-thumb" :src="item.imageFileUrl" :alt="item.name" />
            <img v-else class="artist-row-thumb" src="@/assets/img/blip-logo.png" :alt="item.name" />
            <div class="artist-row-info">
              <p class="artist-row-name">{{ item.name }}</p>
              <p class="artist-row-blipname">{{ item.blipName }}</p>
            </div>
            <base-button v-if="editable" @click="removeArtist(item.unitId)" type="danger" size="sm" class="artist-row-remove">
              <i class="tim-icons icon-simple-remove" />
            </base-button>
          </div>
        </draggable>
        <div v-if="loading && !units.length" class="empty-state">
          <i class="tim-icons icon-refresh-01 empty-state-icon empty-state-icon-spin" />
          <p class="empty-state-title">아티스트 목록을 불러오는 중입니다</p>
        </div>
        <div v-else-if="!loading && !units.length" class="empty-state">
          <i class="tim-icons icon-badge empty-state-icon" />
          <p class="empty-state-title">아직 추가된 아티스트가 없습니다</p>
          <p class="empty-state-desc">
            {{ editable ? '상단의 "아티스트 추가" 버튼을 눌러 이 카테고리에 아티스트를 등록해주세요.' : "이 카테고리는 수정할 수 없습니다." }}
          </p>
        </div>
      </card>

      <artist-group-add-modal
        v-if="editable"
        :visible.sync="artistAddVisible"
        :show-members="false"
        group-section-desc="체크한 그룹이 이 카테고리에 추가됩니다."
        groups-query="isVisible=1"
        :preselected-group-unit-ids="units.map((unit) => unit.unitId)"
        @apply="onArtistAddApply"
      />
    </div>
  </div>
</template>
<script>
import { Card, BaseButton } from "@/components/index";
import { Toast } from "@/util/Toast";
import draggable from "vuedraggable";
import ArtistGroupAddModal from "@/pages/Components/ArtistGroupAddModal.vue";

export default {
  components: {
    Card,
    BaseButton,
    draggable,
    ArtistGroupAddModal,
  },
  async created() {
    if (!this.$route.query.categoryId) {
      Toast.error("잘못된접근입니다");
      history.go(-1);
      return;
    }

    this.categoryId = this.$route.query.categoryId;
    this.categoryKey = this.$route.query.categoryKey || null;
    await this.getData();
  },
  data() {
    return {
      categoryId: null,
      categoryKey: null,
      units: [],
      artistAddVisible: false,
      loading: true,
    };
  },
  computed: {
    editable() {
      // 현재는 categoryKey가 "popular"인 카테고리만 아티스트 추가/정렬/제거가 가능하다.
      // 다음 스콥부터 다른 카테고리도 순차적으로 지원할 예정.
      return this.categoryKey === "popular";
    },
  },
  methods: {
    async getData() {
      this.loading = true;
      return this.$axios
        .get(`/webArtistCategories/${this.categoryId}/units`)
        .then((res) => {
          this.units = res.data.data.map((unit) => {
            if (unit.imageFileId) {
              unit.imageFileUrl = window.Endpoints.imageServerUrl + unit.imageFileId;
            }
            return unit;
          });
        })
        .catch(() => {
          Toast.error("아티스트 목록을 불러오는 중 에러가 발생하였습니다");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onArtistAddApply({ groupUnits }) {
      groupUnits.forEach((group) => {
        if (this.units.some((unit) => unit.unitId === group.unitId)) return;
        if (group.imageFileId) {
          group.imageFileUrl = window.Endpoints.imageServerUrl + group.imageFileId;
        }
        this.units.push(group);
      });
    },
    removeArtist(unitId) {
      if (!this.editable) return;
      this.units = this.units.filter((unit) => unit.unitId !== unitId);
    },
    goList() {
      this.$router.push("/blipArtist/blipArtistCategories");
    },
    async save() {
      if (!this.editable) return;
      const units = this.units.map((unit) => ({ unitId: unit.unitId }));
      return this.$axios
        .put(`/webArtistCategories/${this.categoryId}/units`, { units })
        .then(() => {
          Toast.success("저장되었습니다");
          this.getData();
        })
        .catch(() => {
          Toast.error("저장 중 에러가 발생하였습니다");
        });
    },
  },
};
</script>
<style scoped>
.category-detail-shell {
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
}
::v-deep .artist-list-card .card-body {
  padding: 8px 20px;
}
::v-deep .readonly-banner .card-body {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(253, 160, 74, 0.12);
  border: 1px solid rgba(253, 160, 74, 0.4);
  border-radius: 8px;
  color: #fda04a;
  font-size: 13px;
  text-align: left;
}
.artist-row-handle-disabled {
  cursor: default;
  opacity: 0.35;
}
.artist-row-handle-disabled:active {
  cursor: default;
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 20px;
  text-align: center;
}
.empty-state-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
  border-radius: 50%;
  background: rgba(29, 140, 248, 0.12);
  color: #1d8cf8;
  font-size: 24px;
}
.empty-state-icon-spin {
  animation: empty-state-spin 1s linear infinite;
}
@keyframes empty-state-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.empty-state-title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
}
.empty-state-desc {
  margin: 0 0 20px;
  font-size: 13px;
  color: #9a9a9a;
}
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.toolbar-actions {
  display: flex;
  gap: 10px;
}
.btn-content {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-content i {
  font-size: 14px;
  line-height: 1;
}
.toolbar-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.05);
  color: #9a9a9a;
  font-size: 12px;
}
.hint-drag-icon {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  background: rgba(29, 140, 248, 0.15);
  color: #1d8cf8;
}
.hint-drag-icon i {
  font-size: 9px;
  line-height: 1;
}
.hint-drag-icon i:first-child {
  margin-bottom: -2px;
}
.artist-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: background-color 0.15s ease;
}
.artist-row:last-child {
  border-bottom: none;
}
.artist-row:hover {
  background-color: rgba(255, 255, 255, 0.04);
}
.artist-row-ghost {
  opacity: 0.35;
}
.artist-row-dragging {
  background-color: rgba(29, 140, 248, 0.08);
}
.artist-row-handle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 24px;
  color: #6c6c6c;
  cursor: grab;
}
.artist-row-handle:active {
  cursor: grabbing;
}
.artist-row-handle i {
  font-size: 11px;
  line-height: 1;
}
.artist-row-handle i:first-child {
  margin-bottom: -2px;
}
.artist-row-order {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: #cbcbcb;
  font-size: 13px;
  font-weight: 700;
}
.artist-row-thumb {
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 8px;
  object-fit: cover;
}
.artist-row-info {
  flex: 1;
  text-align: left;
  min-width: 0;
}
.artist-row-name {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.artist-row-blipname {
  margin: 0;
  font-size: 12px;
  color: #9a9a9a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.artist-row-remove {
  padding: 6px 10px;
}
</style>
